import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import { Mail, MapPin, Calendar, Phone, GraduationCap, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";
const INTERNSHIP_FORM_URL = "https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=8b6jusaYg0KZdna1WthYypvbokLA_ZtCmYbeFJI7vY1UM0kyVkc3Sko1SkwyNDQyWkRYWENPQktTWi4u&origin=Invitation&channel=1";

const interests = ["Individual Therapy", "Couples Therapy", "Family Therapy", "LMSW Supervision", "Internship Program", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", interest: interests[0] });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — check your inbox for our reply and a free gift.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", interest: interests[0] });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact — Schedule a Free Consultation"
        description="Reach out to Seven Pillars of Life Therapy in Texas. Free 15-minute consultations for individual, couples, family therapy, autism life skills, and LMSW supervision."
        path="/contact"
      />
      <PageHeader
        overline="Reach Out"
        title={<>Let's <em className="italic text-terracotta">begin.</em></>}
        subtitle="Send us a message or book a free 15-minute consultation. We respond within 1–2 business days."
      />

      <section className="py-16 md:py-24 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="bg-surface border border-sage/25 rounded-2xl p-8 md:p-10" data-testid="contact-form">
              <h2 className="font-display text-2xl md:text-3xl text-forest font-medium mb-2">Send us a message</h2>
              <p className="text-ink/65 mb-8 text-sm">All conversations are confidential.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">Full Name *</label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-name-input"
                    className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">Email *</label>
                  <input
                    type="email"
                    required
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-email-input"
                    className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">Phone</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-phone-input"
                    className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">I'm interested in</label>
                  <select
                    name="interest"
                    value={form.interest}
                    onChange={onChange}
                    data-testid="contact-interest-select"
                    className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                  >
                    {interests.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">Subject</label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  data-testid="contact-subject-input"
                  className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                />
              </div>

              <div className="mt-5">
                <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">Message *</label>
                <textarea
                  required
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message-input"
                  className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-8 inline-flex items-center justify-center bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-8 py-4 font-medium transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending..." : sent ? "Sent — thank you!" : "Send Message"}
              </button>
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-forest text-bone rounded-2xl p-8 md:p-10">
              <p className="overline text-terracotta mb-5">Quick Booking</p>
              <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight">
                Prefer to book directly?
              </h3>
              <p className="mt-4 text-bone/80 leading-relaxed text-sm">
                Schedule a session or free 15-minute consultation through our secure portal.
              </p>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-book-btn"
                className="mt-6 inline-flex items-center bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-6 py-3 text-sm font-medium transition-colors"
              >
                Book Now
              </a>
            </div>

            <div className="bg-surface border border-sage/25 rounded-2xl p-8">
              <p className="overline mb-5 inline-flex items-center gap-2"><GraduationCap size={14} strokeWidth={1.6} /> Students & Interns</p>
              <h3 className="font-display text-xl text-forest font-medium leading-snug">Internship, Field Placement &amp; MSW Placement Requests</h3>
              <p className="mt-3 text-ink/70 leading-relaxed text-sm">
                If you're applying for an internship, field placement, or MSW student placement with Seven Pillars of Life, please use our dedicated request form below.
              </p>
              <a
                href={INTERNSHIP_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-internship-btn"
                className="mt-5 inline-flex items-center gap-2 bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-6 py-3 text-sm font-medium transition-colors"
              >
                Submit Internship Request <ArrowRight size={14} />
              </a>
            </div>

            <div className="bg-surface border border-sage/25 rounded-2xl p-8">
              <p className="overline mb-5">Reach Us</p>
              <ul className="space-y-5 text-ink/80">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-terracotta mt-1 shrink-0" strokeWidth={1.5} />
                  <span>Texas — virtual sessions across the state</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-terracotta mt-1 shrink-0" strokeWidth={1.5} />
                  <a href="mailto:info@7pillarsoflife.com" className="hover:text-terracotta transition link-underline">info@7pillarsoflife.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={18} className="text-terracotta mt-1 shrink-0" strokeWidth={1.5} />
                  <a href="tel:8173698214" className="hover:text-terracotta transition link-underline">817-369-8214</a>
                </li>
                <li className="flex items-start gap-3">
                  <Calendar size={18} className="text-terracotta mt-1 shrink-0" strokeWidth={1.5} />
                  <span>Mon – Fri, 9am – 6pm CT</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
